import { getBorderBase, getFontBase, getPrefixedVar } from '@private/payment';
import styled, { css } from 'styled-components';

import Button from 'components/Button';
import Section from 'components/Form/FormSection';
import { Body as SectionBody } from 'components/Form/FormSection/styled';
import Link from 'components/Link';
import ModalContainer from 'components/ModalContainer';
import { Tabs } from 'components/Tabs';
import Typography from 'components/Typography';

export const PageContainer = styled.div`
  width: 100%;
  padding: 24px 18px 24px 32px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  scrollbar-gutter: stable;
  flex: 1 1 auto;
  scroll-behavior: smooth;
`;

export const ViewTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  color: ${getPrefixedVar(['gistView', 'title'])};
`;

export const Filters = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  column-gap: 10px;
  justify-content: space-between;
`;

export const GridTopActionsRow = styled.div`
  flex: 1 1 100%;
  gap: 16px;
  display: flex;
  justify-content: flex-end;

  @media (width <=1500px) {
    width: 100%;
  }
`;

export const RightColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
`;

export const FilterColumn = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const prefix = ['gistView', 'block'];

export const ViewBlockContainer = styled.div`
  padding: 16px;
  background: ${getPrefixedVar(prefix, 'bg')};
  border-radius: ${getPrefixedVar(prefix, 'borderRadius')};
`;

export const ViewBlockTitle = styled(Typography)`
  padding: 0 14px;
  color: ${getPrefixedVar(prefix, 'title')};
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 24px;
`;

export const BodyContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 24px;
`;

export const ActionsRow = styled.div<{ $center?: boolean }>`
  display: flex;
  align-items: center;
  column-gap: 10px;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
`;

// FORM FIELDS CONTAINERS --------------------------------
export const StyledSection = styled(Section)<{ $noGaps?: boolean }>`
  ${SectionBody} {
    display: grid;
    grid-auto-rows: auto;
    grid-row-gap: ${({ $noGaps }) => ($noGaps ? 0 : 10)}px;
  }
`;

export const gridView = css`
  display: grid;
  grid-auto-rows: auto;
  grid-gap: 10px 24px;
  align-items: start;
`;

export const SingleColumn = styled.div`
  ${gridView};
`;

export const DoubleColumn = styled.div`
  ${gridView};

  grid-template-columns: repeat(2, 1fr);
`;

export const TripleColumn = styled.div`
  ${gridView};

  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

export const FourColumnSplit = styled.div`
  ${gridView};

  grid-template-columns: repeat(4, 1fr);
`;

export const FiveColumnSplit = styled.div`
  ${gridView};

  grid-template-columns: repeat(5, 1fr);
`;

export const TwoThirdsColumnSplit = styled.div`
  ${gridView};

  grid-template-columns: calc((100% - 48px) / 3) calc((100% * 2 - 24px) / 3);
`;

export const TwoThirdsColumn = styled.div`
  ${gridView};

  grid-template-columns: 2fr 1fr;
`;
// FORM FIELDS CONTAINERS --------------------------------

export const StyledDangerButton = styled(Button)`
  color: ${getPrefixedVar(['gridView'], 'deleteIcon')};
`;

export const HighlightedText = styled.span<{ $isUnderlined?: boolean }>`
  color: ${getPrefixedVar(['gistView', 'highlightedText'])};

  ${({ $isUnderlined }) =>
    $isUnderlined &&
    css`
      text-decoration: underline;
    `}
`;

export const RedirectLink = styled(Link)`
  ${getFontBase(['gistView', 'redirectLink'])};

  &:hover,
  &:active {
    ${getFontBase(['gistView', 'redirectLink'])};
  }
`;

const modalPrefix = ['gistView', 'modal'];

export const ResetMessage = styled(Typography)`
  color: ${getPrefixedVar(modalPrefix, 'message')};
`;

export const ParameterValue = styled.span`
  color: ${getPrefixedVar(modalPrefix, 'highlightedText')};
`;

export const TabsSection = styled.div<{ $isTransparent?: boolean }>`
  display: flex;
  position: sticky;
  column-gap: 24px;
  top: 0;
  z-index: 21;
  margin-bottom: 16px;

  ${({ $isTransparent }) =>
    !$isTransparent &&
    css`
      background: ${getPrefixedVar(['main', 'bg'])};
    `}
`;

export const TabsContainer = styled(Tabs)`
  flex: 1 1 auto;
  border-bottom-style: solid;
  overflow: auto;
  ${getBorderBase(['components', 'tabs'])}
`;

export const TabButtonWrapper = styled.div`
  white-space: nowrap;
`;

// ADAPTIVE MODAL CONTAINER --------------------------------
export const AdaptiveModalContainer = styled(ModalContainer)`
  @media (width >= 768px) {
    width: 700px;
  }

  @media (width >= 992px) {
    width: 900px;
  }

  @media (width >= 1200px) {
    width: 1100px;
  }
`;
// ADAPTIVE MODAL CONTAINER --------------------------------
